import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatNumber } from '../lib/utils';

function formatAxisDate(isoDate) {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}

function formatTooltipDate(isoDate) {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Stable sort + numeric coercion for Recharts (line must follow chronological order). */
export function buildForecastHistoryChartData(points) {
  return [...(points || [])]
    .map((p) => ({
      snapshot_date: String(p.snapshot_date || ''),
      committed_revenue_chf: Number(p.committed_revenue_chf || 0),
      sales_forecast_chf: Number(p.sales_forecast_chf || 0),
    }))
    .filter((p) => p.snapshot_date)
    .sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
}

function ForecastHistoryTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs shadow-md">
      <p className="font-semibold text-gray-800">{formatTooltipDate(label)}</p>
      <p className="mt-1 font-mono text-gray-700">
        Committed: {formatNumber(row?.committed_revenue_chf)} CHF
      </p>
      <p className="font-mono text-gray-700">
        Sales forecast: {formatNumber(row?.sales_forecast_chf)} CHF
      </p>
    </div>
  );
}

export function ForecastHistoryChart({
  points,
  granularity,
  onGranularityChange,
  loading,
  error,
}) {
  const chartData = useMemo(() => buildForecastHistoryChartData(points), [points]);

  const showDots = chartData.length <= 45;
  const lineDot = showDots ? { r: 3, clipDot: false } : false;

  const yMax = useMemo(() => {
    let max = 0;
    for (const p of chartData) {
      max = Math.max(max, p.committed_revenue_chf, p.sales_forecast_chf);
    }
    return max > 0 ? Math.ceil(max * 1.08) : 1000;
  }, [chartData]);

  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:rounded-lg sm:p-4"
      data-testid="forecast-history-chart"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          Historical forecast tracking (CHF)
        </p>
        <div className="flex gap-1" role="group" aria-label="Chart granularity">
          <button
            type="button"
            className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
              granularity === 'daily'
                ? 'border-gray-800 bg-gray-800 text-white'
                : 'border-gray-200 bg-white text-gray-600'
            }`}
            onClick={() => onGranularityChange('daily')}
            data-testid="forecast-history-granularity-daily"
          >
            Daily
          </button>
          <button
            type="button"
            className={`rounded-md border px-2 py-0.5 text-xs font-medium ${
              granularity === 'monthly'
                ? 'border-gray-800 bg-gray-800 text-white'
                : 'border-gray-200 bg-white text-gray-600'
            }`}
            onClick={() => onGranularityChange('monthly')}
            data-testid="forecast-history-granularity-monthly"
          >
            Monthly
          </button>
        </div>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500" data-testid="forecast-history-loading">
          Loading chart…
        </p>
      ) : null}
      {error && !loading ? (
        <p className="mt-4 text-sm text-red-600" data-testid="forecast-history-error">
          {error}
        </p>
      ) : null}

      {!loading && !error && chartData.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500" data-testid="forecast-history-empty">
          No historical snapshots yet for this period. Snapshots are recorded when deals change and at startup.
        </p>
      ) : null}

      {!loading && !error && chartData.length > 0 ? (
        <div className="mt-3 h-56 w-full sm:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 20, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="snapshot_date"
                type="category"
                padding={{ left: 12, right: 12 }}
                tickFormatter={formatAxisDate}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) => formatNumber(v)}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                width={72}
                domain={[0, yMax]}
              />
              <Tooltip content={<ForecastHistoryTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                formatter={(value) => (value === 'committed_revenue_chf' ? 'Committed revenue' : 'Sales forecast')}
              />
              <Line
                type="monotone"
                dataKey="committed_revenue_chf"
                name="committed_revenue_chf"
                stroke="#1f2937"
                strokeWidth={2}
                connectNulls
                isAnimationActive={false}
                dot={lineDot}
                activeDot={{ r: 4, clipDot: false }}
              />
              <Line
                type="monotone"
                dataKey="sales_forecast_chf"
                name="sales_forecast_chf"
                stroke="#0284c7"
                strokeWidth={2}
                connectNulls
                isAnimationActive={false}
                dot={lineDot}
                activeDot={{ r: 4, clipDot: false }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
}
